import axios from "axios";
import { Envs } from "../conf/envs";
import { getStoredAuthToken } from "./auth-service";

// This sits below the server's 5 MiB limit and leaves room for multipart data.
const TARGET_PHOTO_SIZE_IN_BYTES = Math.floor(4.5 * 1024 * 1024);
const MAX_IMAGE_DIMENSION = 2048;
const SUPPORTED_IMAGE_TYPES = new Set(["image/jpeg", "image/jpg", "image/png"]);

const isFile = (value) =>
	typeof File !== "undefined" && value instanceof File;

const isImageDataUri = (value) =>
	typeof value === "string" && value.startsWith("data:image/");

const getBearerToken = () => {
	const storedToken = getStoredAuthToken();
	if (!storedToken) return null;

	return storedToken.startsWith('"') ? JSON.parse(storedToken) : storedToken;
};

const getFileBaseName = (name) =>
	(name || "photo").replace(/\.[^/.]+$/, "") || "photo";

const imageDataUriToFile = (dataUri, index) => {
	const match = /^data:([^;]+);base64,(.*)$/s.exec(dataUri);
	if (!match || !SUPPORTED_IMAGE_TYPES.has(match[1].toLowerCase())) {
		throw new Error("Use fotografias JPG ou PNG.");
	}

	const raw = atob(match[2]);
	const bytes = new Uint8Array(raw.length);
	for (let offset = 0; offset < raw.length; offset += 1) {
		bytes[offset] = raw.charCodeAt(offset);
	}

	return new File([bytes], `fotografia-antiga-${index + 1}`, {
		type: match[1].toLowerCase(),
	});
};

const loadImage = (file) =>
	new Promise((resolve, reject) => {
		const image = new Image();
		const objectUrl = URL.createObjectURL(file);

		image.onload = () => {
			URL.revokeObjectURL(objectUrl);
			resolve(image);
		};
		image.onerror = () => {
			URL.revokeObjectURL(objectUrl);
			reject(new Error("Não foi possível ler uma das fotografias."));
		};
		image.src = objectUrl;
	});

const createCompressedPhoto = (image, sourceFile, scale, quality) =>
	new Promise((resolve, reject) => {
		const longestSide = Math.max(image.naturalWidth, image.naturalHeight);
		const dimensionScale = Math.min(1, MAX_IMAGE_DIMENSION / longestSide) * scale;
		const width = Math.max(1, Math.round(image.naturalWidth * dimensionScale));
		const height = Math.max(1, Math.round(image.naturalHeight * dimensionScale));
		const canvas = document.createElement("canvas");
		canvas.width = width;
		canvas.height = height;

		const context = canvas.getContext("2d");
		if (!context) {
			reject(new Error("Não foi possível preparar uma das fotografias."));
			return;
		}

		context.drawImage(image, 0, 0, width, height);
		canvas.toBlob(
			(blob) => {
				if (!blob) {
					reject(new Error("Não foi possível comprimir uma das fotografias."));
					return;
				}

				resolve(
					new File([blob], `${getFileBaseName(sourceFile.name)}.jpg`, {
						type: "image/jpeg",
					})
				);
			},
			"image/jpeg",
			quality
		);
	});

const preparePhotoForUpload = async (file) => {
	if (!SUPPORTED_IMAGE_TYPES.has(file.type.toLowerCase())) {
		throw new Error("Use fotografias JPG ou PNG.");
	}

	if (file.size <= TARGET_PHOTO_SIZE_IN_BYTES) return file;

	const image = await loadImage(file);
	const attempts = [
		{ scale: 1, quality: 0.85 },
		{ scale: 1, quality: 0.72 },
		{ scale: 0.8, quality: 0.72 },
		{ scale: 0.65, quality: 0.65 },
	];

	for (const attempt of attempts) {
		const compressed = await createCompressedPhoto(
			image,
			file,
			attempt.scale,
			attempt.quality
		);
		if (compressed.size <= TARGET_PHOTO_SIZE_IN_BYTES) return compressed;
	}

	throw new Error("Uma das fotografias continua demasiado grande depois de ser comprimida.");
};

export const uploadInterventionPhotos = async (photos) => {
	const uploadedPhotoUrls = [];

	for (const [index, originalPhoto] of (photos || []).entries()) {
		const photo = isImageDataUri(originalPhoto)
			? imageDataUriToFile(originalPhoto, index)
			: originalPhoto;

		if (!isFile(photo)) {
			uploadedPhotoUrls.push(photo);
			continue;
		}

		const uploadFile = await preparePhotoForUpload(photo);
		const formData = new FormData();
		formData.append("file", uploadFile);

		const token = getBearerToken();
		const response = await axios.post(`${Envs.API_BASE}/upload/intervention-photo`, formData, {
			headers: token ? { Authorization: `Bearer ${token}` } : undefined,
		});

		if (!response?.data?.uri) {
			throw new Error("O servidor não devolveu o endereço da fotografia enviada.");
		}

		uploadedPhotoUrls.push(response.data.uri);
	}

	return uploadedPhotoUrls;
};
